/* ============================================================
   NotificationDrawer — CB-2 (286c2f72): in-app notification center
   panel. Right-side drawer opened from the Navbar bell. Loads
   GET /api/v1/notifications (newest first, «Показать ещё» paging),
   POST read-all marks everything read. Light theme, both roles.
   s9 (d2de0c1c, audit-6 GAP-C): rows are tappable — an optional
   onNavigate(target) prop deep-links to the kind→surface target
   (customer kinds → the order card, provider kinds → portal,
   stale-dispute admin kinds → /admin/disputes) and closes the
   drawer; null-target rows stay inert.
   UI-2 (584b9b1b, Artem 2026-08-26 «Уведомления не видно, они
   прячутся за блоком контента»): rendered through a portal to
   document.body. The drawer used to mount inline inside the Navbar
   <header>, whose backdrop-filter (plus sticky+z-index) makes it a
   containing block + stacking context for fixed descendants —
   inset:0 collapsed to the navbar strip and the panel painted
   behind the page content. Portalling escapes both traps; overlay,
   Escape handling and row semantics are unchanged.
   ============================================================ */

import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../api/client';
import type { NotificationItem } from '../types/notification';
import {
  canLoadMore,
  canMarkAllRead,
  drawerRowPress,
  notificationEmptyText,
  notificationFooterText,
  notificationRowView,
} from './notificationsView';
import type { NotificationTarget } from './notificationsView';

interface Props {
  open: boolean;
  onClose: () => void;
  /** s9: session role — resolves dual-recipient kinds (ResultDisputed,
   *  CounterOffer*, DisputeResolved) to the recipient's own surface. */
  role?: string | null;
  /** s9: row-tap deep-link handler; without it every row stays inert. */
  onNavigate?: (target: NotificationTarget) => void;
}

const PAGE_SIZE = 20;

const titleStyle: React.CSSProperties = {
  fontSize: 'var(--fs-h3)',
  fontWeight: 'var(--fw-bold)',
  color: 'var(--color-text-primary)',
};

const muted: React.CSSProperties = {
  fontSize: 'var(--fs-caption)',
  color: 'var(--color-text-secondary)',
};

export function NotificationDrawer({ open, onClose, role = null, onNavigate }: Props) {
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [marking, setMarking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reload page 1 every time the drawer opens.
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    api
      .getNotifications(1, PAGE_SIZE)
      .then((list) => {
        setItems(list.items);
        setUnreadCount(list.unreadCount);
        setTotalCount(list.totalCount);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Не удалось загрузить уведомления'))
      .finally(() => setLoading(false));
  }, [open]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const handleLoadMore = useCallback(async () => {
    setLoadingMore(true);
    setError(null);
    try {
      const next = await api.getNotifications(Math.floor(items.length / PAGE_SIZE) + 1, PAGE_SIZE);
      setItems((prev) => {
        const seen = new Set(prev.map((n) => n.id));
        return [...prev, ...next.items.filter((n) => !seen.has(n.id))];
      });
      setUnreadCount(next.unreadCount);
      setTotalCount(next.totalCount);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить уведомления');
    } finally {
      setLoadingMore(false);
    }
  }, [items.length]);

  const handleMarkAllRead = useCallback(async () => {
    setMarking(true);
    setError(null);
    try {
      await api.markNotificationsRead();
      setUnreadCount(0);
      setItems((prev) => prev.map((n) => (n.readAtUtc === null ? { ...n, readAtUtc: new Date().toISOString() } : n)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось отметить уведомления');
    } finally {
      setMarking(false);
    }
  }, []);

  if (!open) return null;

  // UI-2: portal past the backdrop-filtered sticky header so the fixed
  // surface is positioned/stacked against the viewport, not the navbar box.
  return createPortal(
    <div
      role="dialog"
      aria-label="Уведомления"
      style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', justifyContent: 'flex-end' }}
    >
      {/* Overlay */}
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0, 0, 0, 0.28)' }} />

      {/* Panel */}
      <aside
        style={{
          position: 'relative',
          width: 'min(420px, 100vw)',
          height: '100%',
          background: 'var(--color-surface)',
          borderLeft: '1px solid var(--color-separator)',
          padding: 'var(--space-lg) var(--space-lg)',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-md)',
          boxShadow: 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={titleStyle}>Уведомления</h2>
          <div style={{ display: 'flex', gap: 'var(--space-sm)', alignItems: 'center' }}>
            {canMarkAllRead(unreadCount) && (
              <button
                onClick={handleMarkAllRead}
                disabled={marking}
                style={{
                  fontSize: 'var(--fs-caption)',
                  fontWeight: 'var(--fw-medium)',
                  color: 'var(--color-primary)',
                  background: 'none',
                  border: 'none',
                  cursor: marking ? 'default' : 'pointer',
                  padding: '4px 8px',
                }}
              >
                {marking ? 'Отмечаю…' : 'Прочитать все'}
              </button>
            )}
            <button
              onClick={onClose}
              aria-label="Закрыть"
              style={{
                fontSize: 'var(--fs-body)',
                color: 'var(--color-text-secondary)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px 8px',
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {loading && <p style={muted}>Загрузка уведомлений…</p>}
        {error && <p style={{ ...muted, color: 'var(--color-danger)' }}>{error}</p>}

        {!loading && items.length === 0 && !error && (
          <p style={muted}>{notificationEmptyText(totalCount > 0)}</p>
        )}

        {items.length > 0 && (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
            {items.map((item) => {
              const row = notificationRowView(item, new Date(), role);
              const press = drawerRowPress(row, onNavigate !== undefined);
              const handleRowTap = () => {
                if (!press.active || !press.target) return;
                onNavigate?.(press.target);
                onClose();
              };
              return (
                <li
                  key={row.id}
                  role={press.active ? 'button' : undefined}
                  tabIndex={press.active ? 0 : undefined}
                  aria-disabled={press.active ? undefined : true}
                  onClick={press.active ? handleRowTap : undefined}
                  onKeyDown={
                    press.active
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleRowTap();
                          }
                        }
                      : undefined
                  }
                  style={{
                    display: 'flex',
                    gap: 'var(--space-sm)',
                    padding: '10px 12px',
                    borderRadius: 'var(--radius-md)',
                    background: row.unread ? 'var(--color-bg)' : 'transparent',
                    border: '1px solid var(--color-separator)',
                    alignItems: 'flex-start',
                    cursor: press.active ? 'pointer' : 'default',
                  }}
                >
                  <span aria-hidden style={{ fontSize: '16px', lineHeight: '24px' }}>{row.icon}</span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', gap: 'var(--space-sm)', alignItems: 'baseline' }}>
                      <span
                        style={{
                          fontSize: 'var(--fs-caption)',
                          fontWeight: row.unread ? 'var(--fw-semibold)' : 'var(--fw-medium)',
                          color: 'var(--color-text-primary)',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {row.title}
                      </span>
                      <span style={{ ...muted, whiteSpace: 'nowrap', marginLeft: 'auto' }}>{row.timeLabel}</span>
                    </div>
                    <span style={{ ...muted, overflowWrap: 'anywhere' }}>{row.body}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {!loading && canLoadMore(totalCount, items.length) && (
          <button
            onClick={handleLoadMore}
            disabled={loadingMore}
            style={{
              fontSize: 'var(--fs-body)',
              fontWeight: 'var(--fw-medium)',
              color: 'var(--color-primary)',
              background: 'var(--color-surface)',
              border: '1px solid var(--color-separator)',
              borderRadius: 'var(--radius-md)',
              padding: '10px 14px',
              cursor: loadingMore ? 'default' : 'pointer',
            }}
          >
            {loadingMore ? 'Загрузка…' : 'Показать ещё'}
          </button>
        )}

        {!loading && items.length > 0 && (
          <p style={{ ...muted, textAlign: 'center' }}>{notificationFooterText(totalCount, items.length)}</p>
        )}
      </aside>
    </div>,
    document.body,
  );
}
